import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const WorkspaceCurrencyCard = () => {
  const { base_currency: baseCurrency, home_country: homeCountry, currencies, rates, reload } = useCurrency();
  const [base, setBase] = useState(baseCurrency || "usd");
  const [home, setHome] = useState(homeCountry || "US");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/currency", { base_currency: base, home_country: home, rates });
      await reload();
      toast.success("Workspace currency and home country saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="workspace-currency-card" className="mb-6 border border-border bg-white p-4">
      <div className="overline mb-3 text-slate-500">Workspace defaults</div>
      <p className="mb-3 text-xs text-slate-500">
        New inventory items, orders and quotes start in this currency and country, and every mixed-currency
        total is converted to it.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div>
          <Label className="overline text-slate-500">Base currency</Label>
          <Select value={base} onValueChange={setBase}>
            <SelectTrigger data-testid="workspace-currency-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {Object.entries(currencies || {}).map(([code, c]) => (
                <SelectItem key={code} value={code}>{code.toUpperCase()} — {c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline text-slate-500">Home country</Label>
          <Select value={home} onValueChange={setHome}>
            <SelectTrigger data-testid="workspace-country-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {COUNTRIES.map(([code, label]) => (
                <SelectItem key={code} value={code}>{code} — {label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="save-workspace-currency-button" disabled={busy} onClick={save} className="h-10 rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
          Save defaults
        </Button>
      </div>
    </div>
  );
};
